import OpenModal from 'app/components/VideoModal/OpenModal';
import { getContentSvtId } from 'app/utils/contentoUtils';
import Link from 'next/link';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { isExternalURL } from '../../utils/urlUtil';
import ContentItemFigure from './components/ContentItemFigure/ContentItemFigure';
import ContentItemInfo from './components/ContentItemInfo/ContentItemInfo';
import Placeholder from './components/Placeholder/Placeholder';
import { ContentItemLink, ContentItemWrapper, StyledProgressBar } from './ContentItem.styles';
import { getHeading, getSubHeading, getUrl } from './helpers';

const LinkTypeResolver = ({ autoPlay, href, children, videoId }) => {
    if (autoPlay) {
        return (
            <OpenModal videoUrl={href} videoId={videoId}>
                {(handleOpenModal) => children(handleOpenModal)}
            </OpenModal>
        );
    }

    return (
        <Link href={href} passHref>
            {children(null)}
        </Link>
    );
};

const ContentItem = ({
    className,
    index,
    content,
    loadImage = true,
    listId,
    preferThumbnail,
    editModeActivated,
    markedAsDeleted,
    location,
    presentation,
    preferredAccessVersion,
    progress,
    onMarkedAsDeleted,
    onClick,
    itemPosition,
    isInSweden,
    forwardedRef,
    inView,
    autoPlay = false,
    playingNow = false,
    linkToDetailsPage = true,
}) => {
    const [expanded, toggleExpanded] = useState(false);
    const [expansionIconVisible, toggleExpansionIconVisible] = useState(false);
    const [deletedItem, toggleDeletedItem] = useState(markedAsDeleted);

    if (content.placeholder) {
        return <Placeholder content={content} />;
    }

    const url = getUrl(content, preferredAccessVersion);
    const external = isExternalURL(url);
    const inGrid = typeof inView === 'boolean';
    const shouldRender = inView || !inGrid;
    const autoPlayOverlay = autoPlay && !editModeActivated;

    const onItemClick = (e) => {
        if (editModeActivated) {
            e.preventDefault();
            if (!deletedItem) {
                toggleDeletedItem(true);
                onMarkedAsDeleted({
                    videoId: content.item.svtId,
                    titleArticleId: content.item?.parent?.svtId,
                    svtId: content.item?.parent?.svtId,
                });
            }
        }
        if (onClick) {
            onClick(e, itemPosition, getContentSvtId(content.item, preferredAccessVersion), autoPlay, true);
        }
    };

    const onContentItemInfoClick = (e) => {
        if (onClick) {
            onClick(e, itemPosition, getContentSvtId(content.item, preferredAccessVersion), autoPlay, false);
        }
    };

    const onFocus = (e) => {
        toggleExpansionIconVisible(true);
    };

    const heading = getHeading(content);

    return (
        <ContentItemWrapper
            className={className}
            ref={forwardedRef}
            id={playingNow ? 'episode-picker-playing-now' : undefined}
        >
            {/* Only render if content item is in view (intersection observer) */}
            {shouldRender && (
                <>
                    <LinkTypeResolver href={url} autoPlay={autoPlay} videoId={content.item.svtId}>
                        {(inheritedClickHandler) => (
                            <ContentItemLink
                                isExternal={external}
                                aria-label={autoPlay ? heading + ' - starta video' : heading}
                                tabIndex="0"
                                onClick={(e) => {
                                    inheritedClickHandler && inheritedClickHandler();
                                    onItemClick(e, index + 1, content.item.svtId);
                                }}
                                onFocus={onFocus}
                                role={autoPlayOverlay ? 'button' : 'link'}
                                className={`lp_pos${index}`}
                            >
                                <ContentItemFigure
                                    content={content}
                                    preferThumbnail={preferThumbnail}
                                    showPlaceholder={!loadImage}
                                    fadeOut={deletedItem}
                                    showBadges={!editModeActivated}
                                    location={location}
                                    presentation={presentation}
                                    isInSweden={isInSweden}
                                    autoPlayOverlay={autoPlayOverlay}
                                    editModeActivated={editModeActivated}
                                    markedAsDeleted={deletedItem}
                                    playingNow={playingNow}
                                >
                                    {!deletedItem && <StyledProgressBar selected progress={progress} />}
                                </ContentItemFigure>
                            </ContentItemLink>
                        )}
                    </LinkTypeResolver>
                    <ContentItemInfo
                        heading={heading}
                        subHeading={getSubHeading(listId, content, presentation)}
                        longDescription={content.item?.longDescription}
                        external={external}
                        url={url}
                        onContentInfoClick={onContentItemInfoClick}
                        editModeActivated={false}
                        index={index}
                        expanded={expanded}
                        toggleExpanded={toggleExpanded}
                        expansionIconVisible={expansionIconVisible}
                        toggleExpansionIconVisible={toggleExpansionIconVisible}
                        linkToDetailsPage={linkToDetailsPage}
                    />
                </>
            )}
        </ContentItemWrapper>
    );
};

ContentItem.propTypes = {
    content: PropTypes.shape({
        heading: PropTypes.string,
        subHeading: PropTypes.string,
        images: PropTypes.shape({
            portrait: PropTypes.shape({
                id: PropTypes.string,
                changed: PropTypes.number,
                description: PropTypes.string,
            }),
            wide: PropTypes.shape({
                id: PropTypes.string,
                changed: PropTypes.number,
                description: PropTypes.string,
            }),
        }),
        badge: PropTypes.shape({
            altText: PropTypes.string,
            text: PropTypes.string,
            type: PropTypes.string,
        }),
        item: PropTypes.shape({
            svtId: PropTypes.string,
            id: PropTypes.string,
            durationFormatted: PropTypes.string,
            longDescription: PropTypes.string,
            urls: PropTypes.shape({
                svtplay: PropTypes.string,
            }),
            parent: PropTypes.shape({
                id: PropTypes.any,
            }),
            variants: PropTypes.arrayOf(
                PropTypes.shape({
                    accessibility: PropTypes.string,
                    svtId: PropTypes.string,
                })
            ),
            restrictions: PropTypes.shape({
                onlyAvailableInSweden: PropTypes.bool,
            }),
        }),
    }).isRequired,
    index: PropTypes.number.isRequired,
    loadImage: PropTypes.bool,
    listId: PropTypes.string,
    preferThumbnail: PropTypes.bool,
    editModeActivated: PropTypes.bool,
    markedAsDeleted: PropTypes.bool,
    location: PropTypes.string,
    presentation: PropTypes.string,
    preferredAccessVersion: PropTypes.string,
    progress: PropTypes.number,
    onMarkedAsDeleted: PropTypes.func,
    onClick: PropTypes.func,
    autoPlay: PropTypes.bool,
    linkToDetailsPage: PropTypes.bool,
};

export default React.memo(ContentItem);
